import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { BSL_STATUS } from "../utils/sidebarStorage";
import SidebarCategory from "./SidebarCategory";
import SidebarOverview from "./SidebarOverview";
import {
  getCategoryKey,
  getSectionTitle,
  hasAnyPage,
  isBslCategory,
  isOverviewCategory,
} from "../utils/legalSidebar";

function findSectionByDescription(category, description) {
  return (Array.isArray(category?.Sections) ? category.Sections : []).find(
    (section) =>
      String(section?.Description || "").trim().toLowerCase() ===
      String(description || "").trim().toLowerCase(),
  );
}

function createStandaloneCategory({
  category,
  title,
  section,
  suffix,
  useCategoryLogo = false,
}) {
  if (!category || !section) return null;

  return {
    ...category,
    CategoryID: `${getCategoryKey(category)}-${suffix}`,
    Description: title,
    Logo: useCategoryLogo ? category?.Logo || "" : "",
    Sections: [
      {
        ...section,
        SectionID: `${section?.SectionID ?? suffix}-${suffix}`,
        Description: "",
      },
    ],
  };
}

const MAKE_PAYMENT_FEATURE = {
  FeatureID: "new-make-payment",
  Description: "Make Payment",
  Slug: "makepayment",
  IsPage: true,
};

function appendMakePaymentToOverview(category) {
  if (!category) return category;

  const sections = Array.isArray(category?.Sections) ? category.Sections : [];
  let inserted = false;

  const nextSections = sections.map((section) => {
    const features = Array.isArray(section?.Features) ? section.Features : [];

    if (
      features.some(
        (feature) =>
          String(feature?.Slug || "").trim().toLowerCase() === "makepayment",
      )
    ) {
      inserted = true;
      return section;
    }

    const pendingApprovalsIndex = features.findIndex(
      (feature) =>
        String(feature?.Slug || "").trim().toLowerCase() === "pendingapprovals",
    );

    if (pendingApprovalsIndex === -1) return section;

    inserted = true;
    const nextFeatures = [...features];
    nextFeatures.splice(pendingApprovalsIndex + 1, 0, MAKE_PAYMENT_FEATURE);

    return { ...section, Features: nextFeatures };
  });

  if (inserted) {
    return { ...category, Sections: nextSections };
  }

  if (!nextSections.length) return category;

  const [firstSection, ...restSections] = nextSections;

  return {
    ...category,
    Sections: [
      {
        ...firstSection,
        Features: [
          ...(Array.isArray(firstSection?.Features) ? firstSection.Features : []),
          MAKE_PAYMENT_FEATURE,
        ],
      },
      ...restSections,
    ],
  };
}

export default function LegalNavigation({
  activeKey,
  bslStatus,
  features,
  onNavigate,
}) {
  const [openSections, setOpenSections] = useState({});
  const { transactionsToApproveCount } = useSelector(
    (state) => state.transferResponse,
  );

  const categories = useMemo(
    () =>
      (Array.isArray(features) ? features : []).filter((category) =>
        hasAnyPage(category),
      ),
    [features],
  );

  const overviewCategory = useMemo(
    () => categories.find((category) => isOverviewCategory(category)),
    [categories],
  );

  const overviewCategoryWithMakePayment = useMemo(
    () => appendMakePaymentToOverview(overviewCategory),
    [overviewCategory],
  );

  const dropdownCategories = useMemo(
    () => {
      const items = [];

      categories.forEach((category) => {
        if (isOverviewCategory(category)) return;

        const categoryTitle = String(category?.Description || "")
          .trim()
          .toUpperCase();

        if (categoryTitle !== "SUYOOL ACCOUNT") {
          items.push(category);
          return;
        }

        const overviewSection = findSectionByDescription(category, "Overview");
        const paymentMethodsSection = findSectionByDescription(
          category,
          "Payment Methods",
        );
        const cashSection = findSectionByDescription(category, "Cash");

        const accountOverviewGroup = createStandaloneCategory({
          category,
          title: category.Description,
          section: overviewSection,
          suffix: "overview",
          useCategoryLogo: true,
        });

        const paymentMethodsGroup = createStandaloneCategory({
          category,
          title: "PAYMENT METHODS",
          section: paymentMethodsSection,
          suffix: "payment-methods",
        });

        const cashGroup = createStandaloneCategory({
          category,
          title: getSectionTitle(cashSection?.Description || "Cash").toUpperCase(),
          section: cashSection,
          suffix: "cash-payment",
        });

        if (accountOverviewGroup && hasAnyPage(accountOverviewGroup)) {
          items.push(accountOverviewGroup);
        }

        if (paymentMethodsGroup && hasAnyPage(paymentMethodsGroup)) {
          items.push(paymentMethodsGroup);
        }

        if (cashGroup && hasAnyPage(cashGroup)) {
          items.push(cashGroup);
        }
      });

      return items;
    },
    [categories],
  );

  const toggleSection = (sectionKey) => {
    setOpenSections((current) => ({
      ...current,
      [sectionKey]: !current[sectionKey],
    }));
  };

  return (
    <>
      <SidebarOverview
        category={overviewCategoryWithMakePayment}
        activeKey={activeKey}
        transactionsToApproveCount={transactionsToApproveCount}
        onNavigate={onNavigate}
      />

      {dropdownCategories.map((category) => {
        const categoryKey = getCategoryKey(category);
        const isBsl = isBslCategory(category);

        if (isBsl && bslStatus === BSL_STATUS.rejected) return null;

        return (
          <SidebarCategory
            key={categoryKey}
            category={category}
            activeKey={activeKey}
            open={!!openSections[categoryKey]}
            onToggle={() => toggleSection(categoryKey)}
            disabled={isBsl && bslStatus === BSL_STATUS.pending}
            bslApply={isBsl && bslStatus === BSL_STATUS.apply}
            transactionsToApproveCount={transactionsToApproveCount}
            onNavigate={onNavigate}
          />
        );
      })}
    </>
  );
}
